import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

const ACCENT = '#10B981';
const CYAN = '#06B6D4';
const AMBER = '#F59E0B';

type Ticket = {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  description: string;
  created_at: string;
  message_count?: number;
};

type Message = {
  id: string;
  sender_type: 'user' | 'agent' | 'system';
  sender_name: string;
  message: string;
  created_at: string;
};

function statusColor(s: string) {
  if (s === 'open') return '#F59E0B';
  if (s === 'in_progress') return '#06B6D4';
  if (s === 'resolved') return '#10B981';
  return 'var(--muted)';
}

function priorityColor(p: string) {
  if (p === 'urgent') return '#EF4444';
  if (p === 'high') return '#F97316';
  if (p === 'medium') return '#F59E0B';
  return 'var(--muted)';
}

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CATEGORY_OPTIONS = [
  'Select Category',
  'Getting Started',
  'AI Agents',
  'Pricing & Billing',
  'AI Calling',
  'WhatsApp & Social',
  'GEO & SEO',
  'Technical Issue',
  'Feature Request',
  'Other',
] as const;

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'] as const;

export function SupportPage() {
  const [view, setView] = useState<'list' | 'new' | 'detail'>('list');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('medium');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  const loadTickets = () => {
    void api
      .get<{ tickets: Ticket[] }>('/support/tickets')
      .then((r) => setTickets(r.data.tickets))
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    void api
      .get<{ tickets: Ticket[] }>('/support/tickets')
      .then((r) => setTickets(r.data.tickets))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setSubject('');
    setCategory('');
    setPriority('medium');
    setDescription('');
    setFormError('');
    setSubmitted(false);
    setSubmittedTicketNumber('');
    setView('new');
  };

  const openList = () => {
    setSelectedTicket(null);
    setMessages([]);
    setNewMessage('');
    setView('list');
    loadTickets();
  };

  const openTicket = async (t: Ticket) => {
    try {
      const { data } = await api.get<{ ticket: Ticket; messages: Message[] }>(`/support/tickets/${t.id}`);
      setSelectedTicket(data.ticket);
      setMessages(data.messages);
      setView('detail');
    } catch {
      /* ignore */
    }
  };

  const submitTicket = async () => {
    setFormError('');
    if (!subject.trim() || !category || !description.trim()) {
      setFormError('Subject, category, and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post<{ ticket: Ticket }>('/support/tickets', {
        subject: subject.trim(),
        category,
        priority,
        description: description.trim(),
      });
      setTickets((prev) => [r.data.ticket, ...prev]);
      setSubmittedTicketNumber(r.data.ticket.ticket_number);
      setSubmitted(true);
    } catch {
      setFormError('Submit failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!selectedTicket || !newMessage.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post<{ message: Message }>(`/support/tickets/${selectedTicket.id}/messages`, {
        message: newMessage.trim(),
      });
      setMessages((prev) => [...prev, data.message]);
      setNewMessage('');
      setTickets((prev) =>
        prev.map((x) =>
          x.id === selectedTicket.id ? { ...x, message_count: (x.message_count ?? 0) + 1 } : x
        )
      );
      setSelectedTicket((prev) =>
        prev ? { ...prev, message_count: (prev.message_count ?? 0) + 1 } : prev
      );
    } catch {
      /* ignore */
    } finally {
      setSubmitting(false);
    }
  };

  const closeTicket = async () => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const { data } = await api.patch<{ ticket: Ticket }>(`/support/tickets/${selectedTicket.id}/close`);
      setSelectedTicket(data.ticket);
      setTickets((prev) => prev.map((x) => (x.id === data.ticket.id ? data.ticket : x)));
    } catch {
      /* ignore */
    } finally {
      setSubmitting(false);
    }
  };

  const reopenTicket = async () => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const { data } = await api.patch<{ ticket: Ticket }>(`/support/tickets/${selectedTicket.id}/reopen`);
      setSelectedTicket(data.ticket);
      setTickets((prev) => prev.map((x) => (x.id === data.ticket.id ? data.ticket : x)));
    } catch {
      /* ignore */
    } finally {
      setSubmitting(false);
    }
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '12px 20px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    background: 'none',
    borderBottom: active ? `2px solid ${AMBER}` : '2px solid transparent',
    color: active ? AMBER : 'var(--muted)',
    marginBottom: -1,
  });

  return (
    <div className="odc-page relative z-10" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <style>{`
        @keyframes odc-support-skel { 0%,100%{opacity:0.45} 50%{opacity:0.9} }
      `}</style>
      <div>
        <span
          style={{
            display: 'inline-flex',
            padding: '5px 14px',
            borderRadius: 999,
            border: '1px solid rgba(245,158,11,0.35)',
            background: 'rgba(245,158,11,0.1)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.14em',
            color: AMBER,
            textTransform: 'uppercase',
          }}
        >
          SUPPORT CENTER
        </span>
        <h1 style={{ margin: '12px 0 0', fontSize: 'clamp(1.75rem, 3vw, 2.25rem)', fontWeight: 800, color: 'var(--text)' }}>
          Support Tickets
        </h1>
        <p style={{ margin: '10px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.6 }}>
          Every issue gets expert attention — response guaranteed within 48 hours
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
          {['⚡ 48hr Response', '🎯 Expert Support', '📊 Track Status'].map((t) => (
            <span
              key={t}
              style={{
                border: '1px solid var(--border)',
                borderRadius: 999,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 0 }}>
        <button type="button" style={tabStyle(view === 'list')} onClick={() => openList()}>
          My Tickets
        </button>
        <button type="button" style={tabStyle(view === 'new')} onClick={() => openNew()}>
          New Ticket
        </button>
        {view === 'detail' ? (
          <button type="button" style={tabStyle(true)}>
            Ticket Detail
          </button>
        ) : null}
      </div>

      {view === 'list' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontWeight: 700, color: 'var(--text)' }}>{tickets.length} tickets</span>
            <button type="button" className="odc-btn-primary" onClick={() => openNew()}>
              New Ticket +
            </button>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="odc-card"
                  style={{
                    height: 120,
                    borderRadius: 16,
                    background: 'var(--card2)',
                    border: '1px solid var(--border)',
                    animation: 'odc-support-skel 1.2s ease-in-out infinite',
                  }}
                />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="odc-card" style={{ textAlign: 'center', padding: '60px 24px', borderRadius: 20 }}>
              <div style={{ fontSize: 48 }}>🎉</div>
              <h2 style={{ margin: '16px 0 0', fontSize: 20, fontWeight: 800 }}>No support tickets yet!</h2>
              <p style={{ color: 'var(--muted)', marginTop: 8 }}>Everything looks great. If you need help, raise a support ticket anytime.</p>
              <button type="button" className="odc-btn-primary" style={{ marginTop: 20 }} onClick={() => openNew()}>
                Create First Ticket
              </button>
            </div>
          ) : (
            tickets.map((t) => (
              <button
                key={t.id}
                type="button"
                className="odc-card"
                onClick={() => void openTicket(t)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  marginBottom: 12,
                  padding: 20,
                  borderRadius: 16,
                  cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <span
                        style={{
                          fontFamily: 'ui-monospace, monospace',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '4px 8px',
                          borderRadius: 8,
                          background: 'rgba(6,182,212,0.15)',
                          color: CYAN,
                        }}
                      >
                        {t.ticket_number}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 6,
                          border: `1px solid ${priorityColor(t.priority)}`,
                          color: priorityColor(t.priority),
                        }}
                      >
                        {t.priority}
                      </span>
                    </div>
                    <h3 style={{ margin: '10px 0 0', fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{t.subject}</h3>
                    <span
                      style={{
                        display: 'inline-block',
                        marginTop: 8,
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 6,
                        background: 'var(--card2)',
                        color: 'var(--muted)',
                      }}
                    >
                      {t.category}
                    </span>
                    <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--muted)', lineHeight: 1.5 }}>
                      {t.description.length > 100 ? `${t.description.slice(0, 100)}…` : t.description}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '4px 10px',
                        borderRadius: 8,
                        color: statusColor(t.status),
                        border: `1px solid ${statusColor(t.status)}44`,
                      }}
                    >
                      {t.status.replace('_', ' ')}
                    </span>
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted)' }}>{timeAgo(t.created_at)}</p>
                    <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--muted)' }}>
                      {t.message_count ?? 0} messages
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </>
      ) : null}

      {view === 'new' ? (
        <div>
          <button
            type="button"
            onClick={() => openList()}
            style={{ border: 'none', background: 'none', color: CYAN, cursor: 'pointer', fontSize: 14, marginBottom: 16 }}
          >
            ← Back to tickets
          </button>
          <div className="odc-card" style={{ maxWidth: 720, margin: '0 auto', padding: 28, borderRadius: 20 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Create Support Ticket</h2>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 64 }}>✅</div>
                <h3 style={{ margin: '16px 0 0', fontSize: 20, fontWeight: 800 }}>Ticket Submitted!</h3>
                <p style={{ margin: '12px 0 0', color: 'var(--muted)' }}>Ticket number: {submittedTicketNumber}</p>
                <p style={{ margin: '8px 0 0', color: 'var(--muted)' }}>You will receive a response within 48 hours.</p>
                <button type="button" className="odc-btn-primary" style={{ marginTop: 24 }} onClick={() => openList()}>
                  View My Tickets
                </button>
              </div>
            ) : (
              <>
                <label className="mt-4 block text-xs font-semibold" style={{ color: 'var(--muted)' }}>
                  Subject
                </label>
                <input
                  className="odc-input mt-2"
                  style={{ width: '100%' }}
                  placeholder="e.g. Dashboard is not loading"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitTicket();
                    }
                  }}
                />
                <label className="mt-4 block text-xs font-semibold" style={{ color: 'var(--muted)' }}>
                  Category
                </label>
                <select className="odc-select mt-2 w-full" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt === 'Select Category' ? '' : opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <p className="mt-4 text-xs font-semibold" style={{ color: 'var(--muted)' }}>
                  Priority
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {PRIORITIES.map((p) => {
                    const val = p.toLowerCase() as 'low' | 'medium' | 'high' | 'urgent';
                    const on = priority === val;
                    const col = priorityColor(val);
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPriority(val)}
                        style={{
                          border: `1px solid ${on ? col : 'var(--border)'}`,
                          borderRadius: 999,
                          padding: '8px 16px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: on ? `${col}22` : 'var(--card2)',
                          color: on ? col : 'var(--muted)',
                        }}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
                <label className="mt-4 block text-xs font-semibold" style={{ color: 'var(--muted)' }}>
                  Description
                </label>
                <textarea
                  className="odc-input mt-2"
                  rows={6}
                  style={{ width: '100%', resize: 'vertical' }}
                  placeholder={`Describe the issue in detail...
- What happened?
- When did it happen?
- What error message did you see?
- Attach screenshots (coming soon)`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {formError ? (
                  <p style={{ color: '#EF4444', fontSize: 13, marginTop: 12 }}>{formError}</p>
                ) : null}
                <button
                  type="button"
                  className="odc-btn-primary"
                  style={{ width: '100%', marginTop: 20, justifyContent: 'center' }}
                  disabled={submitting}
                  onClick={() => void submitTicket()}
                >
                  {submitting ? 'Submitting... ⏳' : '🎫 Submit Ticket'}
                </button>
              </>
            )}
          </div>
        </div>
      ) : null}

      {view === 'detail' && selectedTicket ? (
        <div>
          <button
            type="button"
            onClick={() => openList()}
            style={{ border: 'none', background: 'none', color: CYAN, cursor: 'pointer', fontSize: 14, marginBottom: 16 }}
          >
            ← Back to tickets
          </button>
          <div className="odc-card" style={{ padding: 24, borderRadius: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 18, fontWeight: 800, color: CYAN }}>
                {selectedTicket.ticket_number}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 8,
                  color: statusColor(selectedTicket.status),
                  border: `1px solid ${statusColor(selectedTicket.status)}44`,
                }}
              >
                {selectedTicket.status.replace('_', ' ')}
              </span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 10px',
                  borderRadius: 8,
                  color: priorityColor(selectedTicket.priority),
                  border: `1px solid ${priorityColor(selectedTicket.priority)}44`,
                }}
              >
                {selectedTicket.priority}
              </span>
            </div>
            <h2 style={{ margin: '14px 0 0', fontWeight: 800, fontSize: 22, color: 'var(--text)' }}>{selectedTicket.subject}</h2>
            <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--muted)' }}>
              {selectedTicket.category} · {timeAgo(selectedTicket.created_at)} · {messages.length} messages
            </p>
          </div>

          <div
            className="odc-card"
            style={{
              padding: 20,
              borderRadius: 20,
              maxHeight: 400,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {messages.map((m) => {
              if (m.sender_type === 'system') {
                return (
                  <div key={m.id} style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
                      🔔 {m.message}
                    </p>
                    <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--muted)' }}>{timeAgo(m.created_at)}</p>
                  </div>
                );
              }
              if (m.sender_type === 'user') {
                return (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: 'flex-end',
                      maxWidth: '75%',
                      marginLeft: 'auto',
                      background: 'rgba(16,185,129,0.1)',
                      border: '1px solid rgba(16,185,129,0.2)',
                      borderRadius: '16px 16px 4px 16px',
                      padding: '12px 16px',
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 10, color: 'var(--muted)' }}>You</p>
                    <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{m.message}</p>
                    <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--muted)' }}>{timeAgo(m.created_at)}</p>
                  </div>
                );
              }
              return (
                <div
                  key={m.id}
                  style={{
                    alignSelf: 'flex-start',
                    maxWidth: '75%',
                    background: 'var(--card2)',
                    border: '1px solid var(--border)',
                    borderRadius: '16px 16px 16px 4px',
                    padding: '12px 16px',
                  }}
                >
                  <p style={{ margin: 0, fontSize: 10, color: ACCENT }}>{m.sender_name}</p>
                  <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{m.message}</p>
                  <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--muted)' }}>{timeAgo(m.created_at)}</p>
                </div>
              );
            })}
          </div>

          <div className="odc-card" style={{ marginTop: 16, padding: 20, borderRadius: 20 }}>
            {selectedTicket.status === 'closed' ? (
              <>
                <p style={{ color: 'var(--muted)', fontSize: 14 }}>This ticket is closed. Please create a new ticket for a new issue.</p>
                <button type="button" className="odc-btn-ghost" style={{ marginTop: 12 }} disabled={submitting} onClick={() => void reopenTicket()}>
                  Reopen Ticket
                </button>
              </>
            ) : (
              <>
                <textarea
                  className="odc-input"
                  rows={3}
                  style={{ width: '100%' }}
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                  <button
                    type="button"
                    className="odc-btn-primary"
                    disabled={!newMessage.trim() || submitting}
                    onClick={() => void sendReply()}
                  >
                    Send Reply
                  </button>
                  <button type="button" className="odc-btn-ghost" disabled={submitting} onClick={() => void closeTicket()}>
                    Close Ticket
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
